import Image from 'next/image';
import { useRouter } from 'next/router';
import { useAppSelector } from '../../../hooks';
import { IMainFooter } from '../../../models/strapi.models';
import { localeFullCode } from '../../../shared/useUtils';
import { IAppPage } from '../../../store/slices/appSlice';
import { FooterStyle } from '../../../styles/components/common/layout';

export interface IFooterProps {
  items?: IMainFooter;
  contactMethods?: string[];
}

const Footer = (props: IFooterProps) => {
  const router = useRouter();
  const appPages = useAppSelector((state) => state.app.pages);
  const currentYear: number = new Date().getFullYear();

  const getRoute = (actualPath: string) => {
    return (
      appPages?.find(
        (x: IAppPage) =>
          x && x.destination === actualPath && x.language === router.locale
      )?.source || actualPath
    );
  };

  return (
    <footer className={FooterStyle.footer}>
      <div className={FooterStyle['footer-content-block']}>
        <div
          className={`${FooterStyle['footer-content-slot']} ${FooterStyle['slot-1']}`}>
          <div className={FooterStyle['footer-logo']}>
            <Image
              quality={90}
              src={'/assets/igerent-logo-sq.svg'}
              alt="iGerent footer logo"
              priority={false}
              width={39}
              height={39}
            />
          </div>
        </div>
        <div
          className={`${FooterStyle['footer-content-slot']} ${FooterStyle['slot-2']}`}>
          <p>{props.items?.servicesLabel}</p>
          <ul>
            {props.items?.servicesItems.map((value, index) => {
              return (
                <li key={index}>
                  <a href={getRoute(value.path || '/')}>{value.label}</a>
                </li>
              );
            })}
          </ul>
        </div>
        <div
          className={`${FooterStyle['footer-content-slot']} ${FooterStyle['slot-3']}`}>
          <p>{props.items?.contactLabel}</p>
          <ul>
            {props.contactMethods?.map((value, index) => {
              return <li key={index}>{value}</li>;
            })}
          </ul>
          <div className={FooterStyle['footer-social-icons']}>
            <span
              onClick={() =>
                window.open('https://www.facebook.com/igerent.ip', '_system')
              }>
              <Image
                quality={90}
                src="/assets/images/icons/FacebookIcon.svg"
                priority={false}
                alt="Facebook"
                width={24}
                height={24}
              />
            </span>
            <span
              onClick={() => window.open('https://x.com/igerent', '_system')}>
              <Image
                quality={90}
                src="/assets/images/icons/TwitterIcon.svg"
                priority={false}
                alt="Twitter"
                width={24}
                height={24}
              />
            </span>
            <span
              onClick={() =>
                window.open(
                  'https://www.linkedin.com/company/igerent/',
                  '_system'
                )
              }>
              <Image
                quality={90}
                src="/assets/images/icons/LinkedinIcon.svg"
                priority={false}
                alt="Linkedin"
                width={24}
                height={24}
              />
            </span>
            <span
              onClick={() =>
                window.open('https://www.reddit.com/r/igerent/', '_system')
              }>
              <Image
                quality={90}
                src="/assets/images/icons/reddit.svg"
                priority={false}
                alt="Reddit"
                width={24}
                height={24}
                style={{ filter: 'invert(0)' }}
              />
            </span>
            <span
              onClick={() =>
                window.open(
                  'https://clutch.co/profile/igerent-intellectual-property-services',
                  '_system'
                )
              }>
              <Image
                quality={90}
                src="/assets/images/icons/clutchco.svg"
                priority={false}
                alt="Clutch"
                width={24}
                height={24}
                style={{ filter: 'invert(0)' }}
              />
            </span>
          </div>
        </div>
        <div
          className={`${FooterStyle['footer-content-slot']} ${FooterStyle['slot-4']}`}>
          <div className={FooterStyle['footer-payments-title']}>
            <Image
              quality={90}
              src={'/assets/images/icons/ShieldIcon.svg'}
              priority={false}
              width={12}
              height={15}
              alt="Pay safely"
            />
            <p>{props.items?.paymentLabel}</p>
          </div>
          <div className={FooterStyle['footer-payments-methods']}>
            <Image
              quality={90}
              src={'/assets/images/common/payment-methods.svg'}
              priority={false}
              width={282}
              height={25}
              alt="Pay safely"
            />
          </div>
          <div
            className={`${FooterStyle['footer-payments-methods']} ${FooterStyle['footer-payments-methods-inline']}`}>
            <Image
              quality={90}
              src={'/assets/images/common/inta-logo.png'}
              priority={false}
              width={120}
              height={48}
              alt="Pay safely"
            />
            {/* TrustBox widget - Mini */}
            <div
              className="trustpilot-widget"
              data-locale={localeFullCode(router.locale || 'en')}
              data-template-id="53aa8807dec7e10d38f59f32"
              data-businessunit-id="57583e6f0000ff00058f82f6"
              data-style-height="78px"
              data-style-width="150px"
              data-theme="dark"
              data-token="7bfe444d-b552-4369-a41e-2fdc6cd8bcf8">
              <a
                href={`https://${
                  router.locale !== 'en' ? router.locale : 'www'
                }.trustpilot.com/review/igerent.com`}
                target="_blank"
                rel="noopener">
                Trustpilot
              </a>
            </div>
            {/* End TrustBox widget */}
          </div>
        </div>

        <div
          className={`${FooterStyle['footer-content-slot']} ${FooterStyle['slot-5']}`}>
          <p>{props.items?.blogPostsLabel || 'Blog'}</p>
          <div className={FooterStyle['footer-blog-posts']}>
            {props.items?.blogPosts &&
              props.items?.blogPosts.length > 0 &&
              props.items?.blogPosts.map((post, index) => (
                <a
                  key={index}
                  href={`/${router.locale !== 'en' ? router.locale + '/' : ''}${
                    post.slug
                  }`}>
                  {post.title}
                </a>
              ))}
          </div>
        </div>
      </div>
      <div className={FooterStyle['footer-copyright']}>
        <div
          className={`${FooterStyle['footer-content-block']} ${FooterStyle['narrow']}`}>
          <div
            className={`${FooterStyle['footer-content-bottom-slot']} ${FooterStyle['slot-1']} is-self-align-center`}>
            © {currentYear} {props.items?.copyright}
          </div>
          <div
            className={`${FooterStyle['footer-content-bottom-slot']} ${FooterStyle['slot-2']} is-self-align-center is-text-align-right`}>
            <a href={getRoute(props.items?.cookiesPolicyItem.path || '/')}>
              {props.items?.cookiesPolicyItem.label}
            </a>
            <a href={getRoute(props.items?.privacyItem.path || '/')}>
              {props.items?.privacyItem.label}
            </a>
            <a href={getRoute(props.items?.termsItem.path || '/')}>
              {props.items?.termsItem.label}
            </a>
          </div>
        </div>
        <div
          className={`${FooterStyle['footer-content-block']} ${FooterStyle['narrow']} ${FooterStyle['footer-disclaimer']} is-text-align-center`}>
          {props.items?.disclaimer}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
